import { tablesApi } from 'entities/table/api/tablesApi';
import { SpecialTypes } from 'shared/lib/extendedComponents/grids/components/itemFormatCell';
import map from 'lodash-es/map';
import { makeAutoObservable, runInAction } from 'mobx';
import { ExplorerEntityTypes, ExplorerModelInterface } from 'entities/types';
import {
  TableLinkData,
  TableLinkModel,
} from 'entities/table/model/tableLinkModel';

export type TableData = {
  id: string;
  name: string;
  last_change_date: string;
  add_date: string;
  add_date_to_favorite: string;
  user_id: string;
  username: string;
  email: string;
  is_favorite: boolean;
  folder_id: string;
  section_id: string;
};

export type CreateTableParams = {
  name: string;
  section_id: string;
  folder_id?: string;
  links: TableLinkData[];
};

export type RestoreTableParams = {
  folder_id?: string;
  section_id: string;
};

export class TableModel implements ExplorerModelInterface {
  public type = ExplorerEntityTypes.TABLE as const;
  public isPending = false;
  public isError = false;

  private _data: TableData | null = null;
  public links: TableLinkModel[] | null = null;
  private _id: string;
  public get id() {
    return this._id;
  }

  public get data() {
    return this._data
      ? {
          id: this._data.id,
          section_id: this._data.section_id,
          folder_id: this._data.folder_id,
          name: this._data.name,
          last_change_date: this._data.last_change_date,
          add_date: this._data.add_date,
          add_date_to_favorite: this._data.add_date_to_favorite,
          is_favorite: this._data.is_favorite,
          username: this._data.username,
          type: SpecialTypes.TABLE,
          size: 0,
          previewUrl: null,
        }
      : null;
  }

  public get linksData() {
    return map(this.links, (link) => link.data);
  }

  constructor(idOrData: TableData | string) {
    if (typeof idOrData === 'string') {
      this._id = idOrData;
    } else {
      this._id = idOrData.id;
      this._data = idOrData;
    }
    makeAutoObservable(this);
  }

  public async fetch({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const _data = await tablesApi.fetch(this._id, { in_trash });

      runInAction(() => {
        this._data = _data;
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public async fetchLinks({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const links = await tablesApi.fetchLinks(this._id, { in_trash });

      runInAction(() => {
        this.links = map(links, (link) => new TableLinkModel(link));
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public async setFavorite(isFavorite: boolean) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      await tablesApi.setFavourite(this._id, isFavorite);

      runInAction(() => {
        if (this._data) {
          this._data.is_favorite = isFavorite;
        }
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      throw e;
    }
  }

  public updateLocally(
    params: Partial<TableData & { links: TableLinkData[] }>
  ) {
    runInAction(() => {
      if (this._data) {
        Object.assign(this._data, params);
      }

      if (params.links) {
        this.links = params.links.map((link) => new TableLinkModel(link));
      }
    });
  }
}
